/**
 * Determines if the provided node is, or has an ancestor of the provided element type.
 * @arg node: The node's acestory to check.
 * @arg type: The type of node to check for.
 * @returns true if the node is, or has an ancestor of, the requested type; false otherwise.
 */
export function ancestorIsType(node: Node, type: typeof Element) {
  if (!node) {
    return false;
  }

  do {
    if (node instanceof type) {
      return true;
    }
    // eslint-disable-next-line no-param-reassign
  } while ((node = node.parentNode as Node));

  return false;
}

/**
 * Checks the incoming URL to see if it is a valid Bixel tracking URL.
 * @arg url: The URL to test.
 * @returns true if the URL is using the bb/dev.bixel.io or bixel/quaxel#.net domains and contains
 * a tracking suffix; false otherwise.
 */
export function isBixelURL(url?: string) {
  if (url && url.length > 0) {
    // bixel.io can be bb.bixel.io or dev.bixel.io
    return (
      /^(https?):\/\/(bb|dev)\.(bixel\.io)\/v\d+\/(track|t)/i.test(url) ||
      // bixelN.net or quaxelN.net can have dev or no prefix
      /^(https?):\/\/(dev)?\.?(bixel\d+\.net|quaxel\d+\.net)\/v\d+\/(track|t)/i.test(url)
    );
  }

  return false;
}

const videoViewHostnames = [
  'view.bombbomb.com',
  'bbemaildelivery.com',
  'view.bbemaildelivery.com',
  'view.bbsv1.net',
  'view.bbsv2.net',
  'view.bbsv3.net',
  'dev.view.bombbomb.com',
  'dev.bbemaildelivery.com',
  'dev.view.bbemaildelivery.com',
  'dev.view.bbsv1.net',
  'dev.view.bbsv2.net',
  'dev.view.bbsv3.net',
];

export function isVideoViewHostname(hostname: string) {
  return videoViewHostnames.includes(hostname);
}

/**
 * Extracts and decodes the redirect URL from the query string.
 * @arg queryString: The query string to extract/decode.
 * @returns a string with the decoded URL.
 */
export function parseRedirectUrlFromQueryString(queryString: string): string {
  const visitors = [removeLeadingPunctuation, decodeURIComponent];

  return visitors.reduce((previous, current) => {
    return current(previous);
  }, queryString);
}

/**
 * Removes the first non-letter character from a string.
 * @arg url: The string to manipulate.
 * @returns the string with the first non-letter character removed.
 */
export function removeLeadingPunctuation(url: string): string {
  return url && url.match(/^\W/g) ? url.substring(1, url.length) : url;
}

/**
 * Removes the last non-letter character from a string.
 * @arg url: The string to manipulate.
 * @returns the string with the last non-letter character removed.
 */
export function removeTrailingPunctuation(url: string): string {
  return url && url.match(/\W$/g) ? url.substring(0, url.length - 1) : url;
}

// TODO: evaluate for URL inclusion
export function shouldSkipURL(url: string): boolean {
  const trimmedUrl = url.trim();

  // full http(s) links should not be skipped
  if (trimmedUrl && /^https?:\/\/[a-zA-Z0-9]+.+$/.test(trimmedUrl)) {
    return false;
  }

  return true;
}

const getVideoIdFromUrlMaxAttempts = 5;

/**
 * Return the video ID from the URL.
 * @param url URL that contains a video ID
 * @returns
 */
export function getVideoIdFromUrl(url: string, attempts = 0): string | null {
  const parsedUrl = new URL(url);
  if (
    isVideoViewHostname(parsedUrl.hostname) &&
    parsedUrl.searchParams.has('p') &&
    parsedUrl.searchParams.get('p') === 'video_land'
  ) {
    return parsedUrl.searchParams.get('id');
  }

  // match bixel URLs that are going to https://bixel?https://videoURL
  if (/^http(s)/i.test(parsedUrl.searchParams.toString()) && attempts < getVideoIdFromUrlMaxAttempts) {
    // bixel links often have a single ending equal sign (=) that needs to be removed
    const decodedUrl = decodeURIComponent(parsedUrl.searchParams.toString().replace(/={1}$/, ''));
    return getVideoIdFromUrl(decodedUrl, attempts + 1);
  }

  if (attempts === getVideoIdFromUrlMaxAttempts) {
    console.warn('Could not find video ID: too many parse attempts');
  }

  return null;
}
